import { Button, LinearProgress, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import { useQueryClient } from '@tanstack/react-query';

import Modal from 'components/v2/modal/Modal';
import { InputField } from 'components/v2/input/InputField';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import { MODAL_WIDTH } from 'constants/ui/style';
import { useLocalization } from 'localization';
import { SwitchField } from '../../../../components/v2/switch/SwitchField';
import { UserRoles } from '../../../../constants/enums/status';
import { InputSelectField } from '../../../../components/v2/input/InputSelectField';
import { updateUserValidationSchema } from '../../../../components/v2/division/form/schema/usersSchema';
import { IUpdateProfileParams } from '../../../../api';
import notifyHandler from '../../../../utils/notifyHandler';
import { updateUserMutation, userQueryKey } from '../../../../api/users/query';
import UserGearIcon from '../../../../components/v2/icons/UserGearIcon';
import { userRoles } from '../../../../constants/options/userOptions';
import { useAbortController } from 'hooks/general/useAbortController';
import InputPhoneNumber from 'components/v2/input/InputPhoneNumber';

const UpdateUserModal = () => {
  const { t } = useLocalization();
  const { toggleModal, isModalOpen, getModalData } = MODALS_STORE.userUpdate();
  const { user: targetUser, branchId } = getModalData(MODAL_ID.USER_UPDATE);
  const queryClient = useQueryClient();
  const { mutateAsync: updateUserAsync } = updateUserMutation();
  const [exceptionMessage, setExceptionMessage] = useState<string>('');

  const { signal } = useAbortController();

  const methods = useForm({
    defaultValues: {
      firstName: targetUser.firstName || '',
      lastName: targetUser.lastName || '',
      email: targetUser.email || '',
      phone: targetUser.phone || '',
      showPrices: targetUser.settings?.showPrices,
      role: targetUser.isAdmin ? UserRoles.ADMIN : UserRoles.USER,
    },
    resolver: yupResolver(updateUserValidationSchema()),
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting: isFormSubmitting },
    getFieldState,
  } = methods;

  //#region Methods

  async function submitAsync() {
    setExceptionMessage('');

    // Clean up error message
    setExceptionMessage('');

    const payload: Partial<IUpdateProfileParams> = {};
    const values = getValues();

    payload.firstName = values.firstName || '';
    payload.lastName = values.lastName || '';
    payload.email = values.email || '';
    payload.phone = values.phone || '';
    payload.settings = {
      showPrices: values.showPrices,
    };

    const roleFieldState = getFieldState('role');
    let isAdmin: boolean | undefined;
    if (roleFieldState.isDirty || roleFieldState.isTouched) {
      isAdmin = values.role === UserRoles.ADMIN;
    }

    await updateUserAsync(
      {
        userId: targetUser.id,
        payload: {
          ...payload,
          branchId: branchId,
          isAdmin,
        },
        signal,
      },
      {
        onSuccess: () => {
          notifyHandler({
            type: 'success',
            content: t('common.messages.userXHasBeenUpdatedSuccessfully', { email: targetUser.email }),
          });
          queryClient.invalidateQueries({ queryKey: userQueryKey.userAccesses });
          toggleModal(MODAL_ID.USER_UPDATE);
        },
        onError: () => {
          setExceptionMessage(t('common.messages.somethingHappenedWhileUpdatingUserX', { ...targetUser }));
        },
      }
    );
  }

  function isLoading(): boolean {
    return isFormSubmitting;
  }

  //#endregion

  return (
    <Modal
      modalTitle={
        <>
          <UserGearIcon fontSize={'1.25rem'} /> {targetUser.email}
        </>
      }
      isOpen={isModalOpen(MODAL_ID.USER_UPDATE)}
      toggleModal={() => toggleModal(MODAL_ID.USER_UPDATE)}
      width={MODAL_WIDTH.sm}
      isLoading={isLoading()}
      hideFooter={true}
    >
      <form
        noValidate
        onSubmit={handleSubmit(submitAsync, errors => {
          setExceptionMessage(t('common.messages.someFormFieldsAreInvalidPleaseCheck'));
        })}
      >
        {isLoading() && <LinearProgress style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1101 }} />}
        <Stack>
          <Stack gap={'1.25rem'}>
            {exceptionMessage && (
              <Alert
                severity="error"
                onClose={() => {
                  setExceptionMessage('');
                }}
              >
                {exceptionMessage}
              </Alert>
            )}

            <InputSelectField
              name="role"
              control={control}
              options={userRoles}
              label={t('modals.userUpdate.userRole')}
              menuHeight={600}
              loading={isLoading()}
              required={true}
              disabled={isLoading()}
            />
            <InputField
              control={control}
              label="modals.userUpdate.firstName"
              required
              name="firstName"
              disabled={isLoading()}
            />
            <InputField
              control={control}
              label="modals.userUpdate.lastName"
              required
              name="lastName"
              disabled={isLoading()}
            />
            <InputField
              control={control}
              label="modals.userUpdate.emailAddress"
              required
              name="email"
              disabled={isLoading()}
            />
            <InputPhoneNumber
              name="phone"
              label={t('modals.userUpdate.phoneNumber')}
              control={control}
              required
              disabled={isLoading()}
            />
            <SwitchField
              label="modals.userUpdate.seePrices"
              control={control}
              name="showPrices"
              disabled={isLoading()}
            />
            <Button type="submit" loading={isLoading()} fullWidth variant="contained" style={{ height: '3.5rem' }}>
              {t('common.terms.saveChanges')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
};
export default UpdateUserModal;
